import React from 'react';
import { Box, Text, Tooltip } from '@mantine/core';
import {
    IconAlertTriangle,
    IconCircleCheck,
    IconInfoCircle,
    IconSquareRoundedX,
} from '@tabler/icons-react';

import { When } from '../../common';
import PriceRow from './PriceRow';
import {
    shouldShowPnlPriceInput,
    shouldShowCashbuildPriceInput,
} from '../../../helpers/storeHelpers';
import { Store } from '../../../codegen/graphql';

type Props = {
    competitorPrice: string;
    proposedPrice: string;
    costPrice: string;
    storeCostPrice?: string | null;
    proposedMargin: number;
    isMarginPositive: boolean;
    currency: string;
    previousCashbuildPrice: string;
    previousCashbuildMargin: string;
    previousPnlPrice: string;
    previousPnlMargin: string;
    benchmarkMargin: string;
    stores?: Store[];
    completed?: boolean | null;
    latestStatus?: string;
    hideCostAndMargins?: boolean;
    isProposedBelowCost?: boolean;
};

function PriceSectionFlat({
    competitorPrice,
    proposedPrice,
    costPrice,
    storeCostPrice,
    proposedMargin,
    isMarginPositive,
    currency,
    previousCashbuildPrice,
    previousCashbuildMargin,
    previousPnlPrice,
    previousPnlMargin,
    benchmarkMargin,
    stores = [],
    completed,
    latestStatus,
    hideCostAndMargins = false,
    isProposedBelowCost = false,
}: Props) {
    const proposedMarginDisplay = (
        <When
            condition={isMarginPositive}
            fallback={
                <When
                    condition={Number.isFinite(proposedMargin)}
                    fallback={
                        <Text className="font-bold text-[#7D8FB3]">N/A</Text>
                    }
                >
                    <Box className="flex items-center gap-2">
                        <IconAlertTriangle
                            className="text-amber-500"
                            size={20}
                        />
                        <Text className="font-bold text-amber-600">
                            {parseFloat(`${proposedMargin}`).toFixed(2)}%
                        </Text>
                    </Box>
                </When>
            }
        >
            <Box className="flex items-center gap-2">
                <IconCircleCheck className="text-green-500" size={20} />
                <Text className="font-bold text-green-500">
                    {Number.isFinite(proposedMargin)
                        ? `${parseFloat(`${proposedMargin}`).toFixed(2)}%`
                        : 'N/A'}
                </Text>
            </Box>
        </When>
    );

    const proposedPriceLabel = isProposedBelowCost ? (
        <div className="flex items-center gap-1">
            <span>PROPOSED PRICE</span>
            <Tooltip
                label="The proposed price is below cost (ex VAT)"
                withArrow
                position="top"
                zIndex={700}
            >
                <IconInfoCircle
                    size={14}
                    className="cursor-help text-[#7D8FB3]"
                />
            </Tooltip>
        </div>
    ) : (
        'PROPOSED PRICE'
    );

    const proposedPriceDisplay = isProposedBelowCost ? (
        <Box className="flex items-center gap-2 text-red-600">
            <IconSquareRoundedX className="text-red-600" size={20} />
            <Text className="font-bold text-red-600">
                {currency} {proposedPrice}
            </Text>
        </Box>
    ) : (
        `${currency} ${proposedPrice}`
    );

    const showCashbuildPrice =
        stores.length === 0 || shouldShowCashbuildPriceInput(stores);
    const showPnlPrice = stores.length > 0 && shouldShowPnlPriceInput(stores);

    const priceLabel =
        completed && latestStatus === 'Approved'
            ? 'PREVIOUS PRICE'
            : 'CURRENT PRICE';

    const marginLabel =
        completed && latestStatus === 'Approved'
            ? 'PREVIOUS MARGIN'
            : 'CURRENT MARGIN';

    return (
        <div className="pb-2">
            <div className="flex h-full">
                <PriceRow
                    label="COMPETITOR PRICE"
                    value={`${currency} ${competitorPrice}`}
                />
                <PriceRow
                    label={proposedPriceLabel}
                    value={proposedPriceDisplay}
                    showDivider={!hideCostAndMargins}
                />
                {!hideCostAndMargins && (
                    <>
                        <PriceRow
                            label={
                                storeCostPrice &&
                                storeCostPrice !== costPrice ? (
                                    <div className="flex items-center gap-1">
                                        <span>COST PRICE</span>
                                        <Tooltip
                                            label={
                                                <>
                                                    <strong>
                                                        Store cost price:
                                                    </strong>{' '}
                                                    {currency} {storeCostPrice}
                                                    <br />
                                                    <strong>
                                                        Highest in AZ:
                                                    </strong>{' '}
                                                    {currency} {costPrice}
                                                </>
                                            }
                                            multiline
                                            w={260}
                                            withArrow
                                            position="top"
                                            zIndex={700}
                                        >
                                            <IconInfoCircle
                                                size={14}
                                                className="text-[#7D8FB3] cursor-help"
                                            />
                                        </Tooltip>
                                    </div>
                                ) : (
                                    'COST PRICE'
                                )
                            }
                            value={`${currency} ${costPrice}`}
                        />
                        <PriceRow
                            label="PROPOSED MARGIN"
                            value={proposedMarginDisplay}
                            showDivider={false}
                        />
                    </>
                )}
            </div>

            {showCashbuildPrice && (
                <div className="rounded-lg overflow-hidden border border-[#e6e9ef]">
                    <div className="flex">
                        <div className="p-4 flex items-center bg-white min-w-[200px]">
                            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                                CASHBUILD PRICE
                            </Text>
                        </div>
                        <div className="flex-1 bg-[#f4f5f7] rounded-l-lg">
                            <div className="flex h-full">
                                <PriceRow
                                    label={priceLabel}
                                    value={`${currency} ${previousCashbuildPrice}`}
                                    showDivider={!hideCostAndMargins}
                                />
                                {!hideCostAndMargins && (
                                    <>
                                        <PriceRow
                                            label={marginLabel}
                                            value={previousCashbuildMargin}
                                        />
                                        <PriceRow
                                            label="BENCHMARK MARGIN"
                                            value={benchmarkMargin}
                                            showDivider={false}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {showPnlPrice && (
                <div
                    className={`${
                        showCashbuildPrice ? 'mt-3' : ''
                    } rounded-lg overflow-hidden border border-[#e6e9ef]`}
                >
                    <div className="flex">
                        <div className="p-4 flex items-center bg-white min-w-[200px]">
                            <Text className="uppercase text-xs text-[#7D8FB3] font-bold">
                                P&L PRICE
                            </Text>
                        </div>
                        <div className="flex-1 bg-[#f4f5f7] rounded-l-lg">
                            <div className="flex h-full">
                                <PriceRow
                                    label={priceLabel}
                                    value={`${currency} ${previousPnlPrice}`}
                                    showDivider={!hideCostAndMargins}
                                />
                                {!hideCostAndMargins && (
                                    <>
                                        <PriceRow
                                            label={marginLabel}
                                            value={previousPnlMargin}
                                        />
                                        <PriceRow
                                            label="BENCHMARK MARGIN"
                                            value={benchmarkMargin}
                                            showDivider={false}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PriceSectionFlat;
